import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import uniq from 'lodash/uniq';
import { RefreshableItem, translateRefreshableItemsToScopes } from '../models/refreshableItem';
import { ERR_PARAM_REFRESHABLE_ITEMS_INVALID } from '../utilities/error';
import { isEnumType, ValidateProductParams } from '../utilities/urlParameters';
import { containsInvalidRefreshableItem, getValidRefreshableItems } from '../utilities/validators';
import { DeprecatedProductsTypes, ProductsType } from './models/products';

export const ERR_PARAM_PRODUCTS_INVALID = {
  errorStatus: ErrorStatus.BAD_REQUEST,
  errorReason: ErrorReason.INVALID_PARAMETER_PRODUCTS,
};

const getProductNames = (productsUrlParameter: string) => {
  const productNames = productsUrlParameter.split(',').map((product) => {
    switch (product) {
      case DeprecatedProductsTypes.ASSETS:
      case ProductsType.INVESTMENTS:
        return ProductsType.INVESTMENTS;
      case DeprecatedProductsTypes.LIABILITIES:
      case ProductsType.LOANS:
        return ProductsType.LOANS;
      case ProductsType.ACCOUNT_CHECK:
        return ProductsType.ACCOUNT_CHECK;
      case ProductsType.TRANSACTIONS:
        return ProductsType.TRANSACTIONS;
      default:
        return '';
    }
  });
  const uniqProductNames = uniq(productNames);

  return uniqProductNames;
};

const getValidatedProducts = (products: string[]) => {
  return products.filter(isEnumType(ProductsType));
};

export const mappedProductsToRefreshableItems = {
  [ProductsType.INVESTMENTS]: [RefreshableItem.INVESTMENT_ACCOUNTS],
  [ProductsType.LOANS]: [RefreshableItem.LOAN_ACCOUNTS],
  [ProductsType.TRANSACTIONS]: [
    RefreshableItem.CHECKING_ACCOUNTS,
    RefreshableItem.CHECKING_TRANSACTIONS,
    RefreshableItem.SAVING_ACCOUNTS,
    RefreshableItem.SAVING_TRANSACTIONS,
    RefreshableItem.CREDITCARD_ACCOUNTS,
    RefreshableItem.CREDITCARD_TRANSACTIONS,
  ],
  [ProductsType.ACCOUNT_CHECK]: [
    RefreshableItem.CHECKING_ACCOUNTS,
    RefreshableItem.SAVING_ACCOUNTS,
    RefreshableItem.IDENTITY_DATA,
  ],
};

export const translateProductsToRefreshableItems = (
  includedProducts: ProductsType[]
): RefreshableItem[] => {
  const refreshableItems = includedProducts.reduce<RefreshableItem[]>((acc, product) => {
    return acc.concat(mappedProductsToRefreshableItems[product]);
  }, []);

  // Merge duplicated refreshable items and return only unique
  return Array.from(new Set(refreshableItems));
};

export const validateParams: ValidateProductParams = (commonParameters, getRawParam) => {
  const errors = [];

  const queryProducts = getRawParam(TinkLinkUrlParameter.PRODUCTS) || '';
  const rawProducts = getProductNames(queryProducts);
  const validProducts = getValidatedProducts(rawProducts);

  if (validProducts.length === 0 || rawProducts.length !== validProducts.length) {
    errors.push(ERR_PARAM_PRODUCTS_INVALID);
  }

  const refreshableItems = getRawParam(TinkLinkUrlParameter.REFRESHABLE_ITEMS);

  if (refreshableItems && containsInvalidRefreshableItem(refreshableItems)) {
    errors.push(ERR_PARAM_REFRESHABLE_ITEMS_INVALID);
  }

  const finalRefreshableItems = refreshableItems
    ? getValidRefreshableItems(refreshableItems)
    : translateProductsToRefreshableItems(validProducts);

  const scope = translateRefreshableItemsToScopes(finalRefreshableItems);

  return {
    parameters: {
      refreshableItems: finalRefreshableItems,
      scope,
      products: validProducts,
    },
    errors,
  };
};
